import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AnalyzeAttemptDto } from "../lib/generated/api-client";
import { getApi } from "../lib/get-api";
import { useDatasources } from "../lib/useDatasources";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import { useAuth } from "../lib/useAuth";
import AnalyseProgressModal from "./AnalyseProgressModal";
import { latestFailedAttempt } from "../lib/after-auth";
import { sheetsReconnectFromAttempt } from "../lib/sheets-reconnect";
import {
  hideSample,
  isDemoDatasource,
  isSampleVisible,
  isSharedDemoDatasource,
  isUserSampleSheet,
  visibleDatasources,
} from "../lib/demo-preference";
import { datasourceDisplayName } from "../lib/sample-label";
import { takePendingSampleAnalyse } from "../lib/pending-sample-analyse";
import { isUiGenerationReady } from "../lib/ui-generation-status";
import { DatasourceCard, Datasource } from "./DatasourceCard";
import { Button } from "./ui/Button";

interface DataSourcesTableProps {
  onAnalyze?: (id: string) => void;
  onGenerateUI?: (id: string) => void;
  onDelete?: (id: string) => void;
  onError?: (error: string) => void;
  onConnect?: () => void;
  className?: string;
}

const DataSourcesTable: React.FC<DataSourcesTableProps> = ({
  onAnalyze,
  onGenerateUI,
  onDelete,
  onError,
  // Kept for callers; "More connections" lives on SheetsConnectLead only.
  onConnect: _onConnect,
  className = "",
}) => {
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [selectedDataSourceId, setSelectedDataSourceId] = useState<
    string | null
  >(null);
  const [showProcessingModal, setShowProcessingModal] =
    useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [processingMessage, setProcessingMessage] = useState<string>("");
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [messageModalContent, setMessageModalContent] = useState<string>("");

  const [analyseAttempt, setAnalyseAttempt] =
    useState<AnalyzeAttemptDto | null>(null);
  const [analyseError, setAnalyseError] = useState<string | null>(null);
  const [analyseElapsed, setAnalyseElapsed] = useState<number>(0);
  const [analyseDatasourceId, setAnalyseDatasourceId] =
    useState<string | null>(null);
  // FAILED attempts, kept per-datasource so a first-run failure survives the
  // modal closing. The list payload never carries FAILED rows, so this overlay
  // is the only source of failure state (cleared on a later SUCCESS/delete).
  const [failedAttempts, setFailedAttempts] = useState<
    Record<string, AnalyzeAttemptDto>
  >({});
  const analyseStartRef = useRef<number | null>(null);
  const analysePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { token, user, guest } = useAuth();
  const canConnectOwn = !!user && !guest;
  const accountId =
    (typeof user?.id === "string" && user.id) ||
    (typeof guest?.id === "string" && guest.id) ||
    null;
  const navigate = useNavigate();
  const [sampleVisible, setSampleVisibleState] = useState(() =>
    isSampleVisible(accountId),
  );

  const api = getApi({ token });

  const { datasources, fetchDatasources, isLoading, isInitialLoad, fetchError } =
    useDatasources();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  // useSearchParams' setter is a new function every URL change — keep it in a
  // ref so the highlight effect does not re-fire when we strip the param.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;
  const highlightAppliedRef = useRef<string | null>(null);
  /** One refetch per highlight id when the store does not yet have that row. */
  const highlightRefetchRef = useRef<string | null>(null);
  /** Resume the sample-flow analyse handoff at most once per mount. */
  const pendingAnalyseTakenRef = useRef(false);

  useEffect(() => {
    const sync = () => setSampleVisibleState(isSampleVisible(accountId));
    sync();
    window.addEventListener("od:sample-visible-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("od:sample-visible-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, [accountId]);

  // Sample create starts analysis before navigate. Take that pending attempt
  // here so the modal/poll path runs without a second createAnalyzeAttempt.
  useEffect(() => {
    if (pendingAnalyseTakenRef.current) return;
    if (analyseDatasourceId != null) return;
    const pending = takePendingSampleAnalyse();
    pendingAnalyseTakenRef.current = true;
    if (!pending?.datasourceId || !pending.attempt?.id) return;
    analyseStartRef.current = Date.now();
    setAnalyseError(null);
    setAnalyseElapsed(0);
    setAnalyseDatasourceId(pending.datasourceId);
    setAnalyseAttempt(pending.attempt);
  }, [analyseDatasourceId]);

  // Guests who opened the demo keep the preference on; signed-in users opt in.
  const listed = visibleDatasources(datasources, {
    showSample: sampleVisible || Boolean(guest),
  }) as Datasource[];

  useEffect(() => {
    // MOSA-553: wait for an attempt id before polling. A PENDING row without
    // an id must not start a 1.5 s timer that calls getAnalyzeAttempt with
    // undefined.
    if (!analyseAttempt?.id || analyseError) return;
    if (analysePollRef.current) clearInterval(analysePollRef.current);

    analysePollRef.current = setInterval(async () => {
      if (!analyseDatasourceId || !analyseAttempt?.id) return;
      try {
        const fresh = await api.analysis.getAnalyzeAttempt(
          analyseDatasourceId,
          analyseAttempt.id,
        );
        setAnalyseAttempt(fresh.data);
        if (analyseStartRef.current) {
          setAnalyseElapsed(
            Math.floor((Date.now() - analyseStartRef.current) / 1000),
          );
        }
        if (fresh.data.status === "SUCCESS") {
          if (analysePollRef.current) clearInterval(analysePollRef.current);
          const okId = analyseDatasourceId;
          setFailedAttempts((prev) => {
            if (!(okId in prev)) return prev;
            const next = { ...prev };
            delete next[okId];
            return next;
          });
          try {
            const generation = await api.ui.generateUi({
              analyzeAttemptId: fresh.data.id,
              prompt: "Generate one page per entity as { page, entities[] }[].",
              async: false,
            });
            // HTTP 201 can still carry FAILED, or a stale RUNNING row that
            // already has panels. Fail only on FAILED / empty unfinished bodies.
            if (!isUiGenerationReady(generation.data)) {
              throw new Error(
                generation.data.status === "FAILED"
                  ? "Page generation failed"
                  : "Page generation did not finish",
              );
            }
          } catch (err) {
            const errorMsg =
              "Analysis finished, but page generation failed. Try Generate pages again.";
            setAnalyseError(errorMsg);
            onError?.(errorMsg);
            console.error(errorMsg, err);
            await fetchDatasources();
            return;
          }
          onAnalyze?.(analyseDatasourceId);
          onGenerateUI?.(analyseDatasourceId);
          await fetchDatasources();
          setTimeout(() => {
            setAnalyseAttempt(null);
            setAnalyseDatasourceId(null);
          }, 800);
        } else if (fresh.data.status === "FAILED") {
          if (analysePollRef.current) clearInterval(analysePollRef.current);
          const failedId = analyseDatasourceId;
          setFailedAttempts((prev) => ({ ...prev, [failedId]: fresh.data }));
          const reconnect = sheetsReconnectFromAttempt(fresh.data);
          if (reconnect) {
            await fetchDatasources();
            setAnalyseAttempt(null);
            setAnalyseDatasourceId(null);
            onError?.(reconnect.title);
            return;
          }
          setAnalyseError(
            (fresh.data as any).errorMessage ||
              "Analysis failed. Check the API logs for details.",
          );
        }
      } catch (err) {
        // Transient network errors are fine; keep polling.
        // eslint-disable-next-line no-console
        console.warn("Polling analyse attempt failed", err);
      }
    }, 1500);

    return () => {
      if (analysePollRef.current) clearInterval(analysePollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyseAttempt?.id, analyseDatasourceId, analyseError]);

  const handleDelete = async (id: string) => {
    try {
      setShowProcessingModal(true);
      await api.datasources.deleteDatasource(id);
      setFailedAttempts((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setMessageModalContent("Data source has been successfully deleted.");
      onDelete?.(id);
      fetchDatasources();
    } catch (error) {
      const errorMsg = "Unable to delete the selected data source.";
      setError(errorMsg);
      onError?.(errorMsg);
      console.error(errorMsg, error);
      setMessageModalContent(errorMsg);
    } finally {
      setShowProcessingModal(false);
      setShowMessageModal(true);
      setShowConfirmDialog(false);
      setSelectedDataSourceId(null);
    }
  };

  const handleAnalyze = async (id: string) => {
    if (analyseDatasourceId != null && analyseError == null) return;
    setAnalyseError(null);
    setAnalyseAttempt(null);
    setAnalyseDatasourceId(id);
    setAnalyseElapsed(0);
    analyseStartRef.current = Date.now();
    try {
      const initial = await api.analysis.createAnalyzeAttempt(id, {
        async: true,
      });
      setAnalyseAttempt(initial.data);
    } catch (error) {
      const errorMsg = "An error occurred while starting the analysis.";
      setAnalyseError(errorMsg);
      onError?.(errorMsg);
      console.error(errorMsg, error);
    }
  };

  const closeAnalyseModal = () => {
    if (analysePollRef.current) clearInterval(analysePollRef.current);
    setAnalyseAttempt(null);
    setAnalyseError(null);
    setAnalyseDatasourceId(null);
  };

  const openConfirmDialog = (id: string) => {
    setSelectedDataSourceId(id);
    setShowConfirmDialog(true);
  };

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false);
    setSelectedDataSourceId(null);
  };

  const closeMessageModal = () => {
    setShowMessageModal(false);
    setMessageModalContent("");
  };

  const closeSample = () => {
    hideSample(accountId);
    setSampleVisibleState(false);
    if (listed.some((datasource) => isDemoDatasource(datasource))) {
      navigate("/data-sources");
    }
  };

  useEffect(() => {
    if (!highlightId || isInitialLoad) return;

    const found = datasources.some(
      (datasource) => datasource.id === highlightId,
    );
    // SPA create → navigate can land here before the list includes the new
    // row. Refetch once. Do not loop if the id truly is gone.
    if (!found) {
      if (highlightRefetchRef.current === highlightId) return;
      highlightRefetchRef.current = highlightId;
      void fetchDatasources();
      return;
    }

    // Once per highlight id — do not re-scroll on every list render.
    // Analysis for a new sample starts in the sample create flow, not here.
    if (highlightAppliedRef.current === highlightId) return;
    highlightAppliedRef.current = highlightId;
    // Attribute selector: escape \ and " so ids with those chars still match.
    const safe = highlightId.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    const node = document.querySelector(
      `[data-datasource-id="${safe}"]`,
    );
    if (node instanceof HTMLElement) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      node.focus({ preventScroll: true });
    }
    setSearchParamsRef.current(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("highlight");
        return next;
      },
      { replace: true },
    );
  }, [highlightId, isInitialLoad, datasources, fetchDatasources]);

  // While the first list is unknown, never flash the empty first-run screen.
  if (isInitialLoad) {
    return <div className="text-center py-8">Loading data sources…</div>;
  }

  if (fetchError && datasources.length === 0) {
    return (
      <div
        className="flex flex-col items-center gap-3 py-8 text-center"
        data-testid="datasources-load-error"
      >
        <p className="text-sm text-od-danger">{fetchError}</p>
        <Button
          variant="primary"
          size="sm"
          onClick={() => void fetchDatasources()}
          loading={isLoading}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-od-danger py-8">{error}</div>;
  }

  // MOSA-695 item 7: with nothing to list, SheetsConnectLead is the one
  // empty-state card on the page. A second card here only repeated its title,
  // its sentence and its button.
  if (listed.length === 0) return null;

  const only = listed[0];
  // MOSA-908. Only the shared Postgres demo uses the "sample project" banner
  // and Hide sample. A Sheets sample the user chose is a real data source:
  // list it under Connected data sources and offer Remove sample on the card.
  const demoOnlyList =
    listed.length === 1 && isSharedDemoDatasource(only);
  const sharedDemoListed = listed.some((datasource) =>
    isSharedDemoDatasource(datasource),
  );

  const cards = listed.map((dataSource) => {
    const thisCardRunning =
      analyseDatasourceId === dataSource.id && !analyseError;
    // Merge the held FAILED attempt (if any) so the card shows failure state
    // after the modal closes; the list payload only carries SUCCESS rows.
    const overlay = failedAttempts[dataSource.id as string];
    const mergedAttempts = overlay
      ? [...(dataSource.analyzeAttempts ?? []), overlay]
      : dataSource.analyzeAttempts;
    const cardDatasource = overlay
      ? { ...dataSource, analyzeAttempts: mergedAttempts }
      : dataSource;
    const analysed = (dataSource.analyzeAttempts?.length ?? 0) > 0;
    const lastFailed = latestFailedAttempt(mergedAttempts);
    const modalAttempt =
      analyseAttempt?.id === overlay?.id ? analyseAttempt : undefined;
    const cardAnalysing =
      thisCardRunning && (modalAttempt?.status ?? "RUNNING") === "RUNNING";
    const highlighted = highlightId != null && dataSource.id === highlightId;
    return (
      <div
        key={dataSource.id}
        data-datasource-id={dataSource.id}
        tabIndex={highlighted ? -1 : undefined}
        className={
          highlighted
            ? "rounded-lg ring-2 ring-od-primary outline-none"
            : undefined
        }
      >
        <DatasourceCard
          datasource={cardDatasource as Datasource}
          analysing={cardAnalysing}
          analyseLabel={
            cardAnalysing
              ? analyseAttempt?.progress?.total
                ? `Analyzing… ${analyseAttempt.progress.done ?? 0}/${analyseAttempt.progress.total}`
                : "Analyzing…"
              : lastFailed
                ? "Retry analysis"
                : analysed
                  ? "Re-sync pages"
                  : "Generate pages"
          }
          onAnalyze={() => handleAnalyze(dataSource.id as string)}
          onDelete={() => openConfirmDialog(dataSource.id as string)}
        />
      </div>
    );
  });

  return (
    <div
      className={`bg-od-surface border border-od-border p-4 md:p-6 rounded-lg w-full ${className}`}
    >
      {demoOnlyList ? (
        <>
          {canConnectOwn ? (
            <div className="mb-4 flex flex-col items-start gap-3">
              <p className="text-od-muted m-0 text-sm">
                This is the sample project. Explore it, or connect your own
                sheet.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate("/connect/sheets")}
                >
                  Connect a Google Sheet
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeSample}
                  data-testid="hide-sample"
                >
                  Hide sample
                </Button>
              </div>
            </div>
          ) : (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <p className="text-od-muted m-0 text-sm">
                This is the sample project. Open a table or the dashboard to
                start.
              </p>
              {!guest && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeSample}
                  data-testid="hide-sample"
                >
                  Hide sample
                </Button>
              )}
            </div>
          )}
          <div>{cards}</div>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 mb-2">
            {/* Section heading: the page h1 lives on SheetsConnectLead.
                "More connections" lives only on SheetsConnectLead so the
                page never shows that link twice. */}
            <h2 className="text-lg font-semibold text-od-text m-0">
              Connected data sources
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              {sampleVisible && sharedDemoListed && !guest && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={closeSample}
                  data-testid="hide-sample"
                  className="text-xs underline decoration-od-border underline-offset-4"
                >
                  Hide sample
                </Button>
              )}
            </div>
          </div>
          <p className="text-od-muted mb-3 text-sm">
            Manage your connected data sources below.
          </p>
          <div className="space-y-3">{cards}</div>
        </>
      )}

      <ConfirmDeleteModal
        open={showConfirmDialog}
        onCancel={closeConfirmDialog}
        onConfirm={() => handleDelete(selectedDataSourceId!)}
        title={
          isUserSampleSheet(
            listed.find((d) => d.id === selectedDataSourceId),
          )
            ? "Remove this sample?"
            : "Disconnect this datasource?"
        }
        confirmLabel={
          isUserSampleSheet(
            listed.find((d) => d.id === selectedDataSourceId),
          )
            ? "Remove sample"
            : "Disconnect"
        }
        body={
          isUserSampleSheet(
            listed.find((d) => d.id === selectedDataSourceId),
          )
            ? "OnlyDash removes the sample from your account. You can add it again later."
            : "OnlyDash forgets the connection and the schema it generated. Your database itself is untouched."
        }
        description={
          datasourceDisplayName(
            listed.find((d) => d.id === selectedDataSourceId),
          ) || undefined
        }
      />

      {(analyseAttempt || analyseError) && (
        <AnalyseProgressModal
          attempt={analyseAttempt}
          elapsedSeconds={analyseElapsed}
          datasourceName={
            datasourceDisplayName(
              listed.find((d) => d.id === analyseDatasourceId),
            ) || null
          }
          error={analyseError}
          onClose={analyseError ? closeAnalyseModal : undefined}
        />
      )}

      {showProcessingModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-50">
          <div className="bg-od-surface p-4 rounded-lg flex items-center gap-3 border border-od-border">
            <div className="loader"></div>
            <span className="text-od-muted text-sm">{processingMessage}</span>
          </div>
        </div>
      )}

      {showMessageModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-50">
          <div className="bg-od-surface p-4 rounded-lg flex flex-col items-center gap-3 border border-od-border">
            <span className="text-od-text text-sm">{messageModalContent}</span>
            <Button
              variant="primary" size="sm" className="text-sm"
              onClick={closeMessageModal}
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataSourcesTable;
